import { capitalize } from 'lodash';

import {
  ActivityChangeOptions,
  ActivityItem,
  ActivityTagItem,
  ActivityUserItem,
} from '@spotify/plugin-confidence-activity-react';
import { createWorkflowUpdatesChangeExtension } from '@spotify/plugin-confidence-workflows-react';

interface ReviewResponse {
  reviewer: string;
  state: string;
}

interface SurfaceReview {
  surface: string;
  responses: ReviewResponse[];
  state: string;
}

interface ReviewsData {
  approvalRequired?: boolean;
  requiredReviews?: SurfaceReview[];
  optionalReviews?: SurfaceReview[];
  status?: string;
  reviewId?: string;
}

const formatState = (state: string | undefined): string => {
  if (!state) return '';
  return capitalize(state.toLowerCase());
};

const formatReviewerLabel = (state: string): string => {
  if (state.toUpperCase() === 'REQUESTED') return 'Requested review from';
  return formatState(state);
};

const collectResponses = (data: ReviewsData | null): Map<string, string> => {
  const map = new Map<string, string>();
  const allReviews = [
    ...(data?.requiredReviews ?? []),
    ...(data?.optionalReviews ?? []),
  ];
  for (const group of allReviews) {
    for (const response of group.responses ?? []) {
      if (response.reviewer) {
        map.set(response.reviewer, response.state);
      }
    }
  }
  return map;
};

interface ChangedReviewer {
  reviewer: string;
  oldState: string | undefined;
  newState: string;
}

const getChangedReviewers = (
  oldValue: ReviewsData | null,
  newValue: ReviewsData | null,
): ChangedReviewer[] => {
  const oldResponses = collectResponses(oldValue);
  const newResponses = collectResponses(newValue);
  const changed: ChangedReviewer[] = [];
  for (const [reviewer, newState] of newResponses) {
    const oldState = oldResponses.get(reviewer);
    if (oldState !== newState) {
      changed.push({ reviewer, oldState, newState });
    }
  }
  return changed;
};

const ReviewsChangeRender = ({ change }: ActivityChangeOptions) => {
  const oldValue = change.oldValue as ReviewsData | null;
  const newValue = change.newValue as ReviewsData | null;

  const statusChanged = oldValue?.status !== newValue?.status;
  const changedReviewers = getChangedReviewers(oldValue, newValue);

  return (
    <>
      {statusChanged && (
        <ActivityItem.Section label="Updated status">
          <ActivityTagItem
            oldValue={formatState(oldValue?.status)}
            newValue={formatState(newValue?.status)}
          />
        </ActivityItem.Section>
      )}
      {changedReviewers.map(({ reviewer, newState }) => (
        <ActivityItem.Section
          key={reviewer}
          label={formatReviewerLabel(newState)}
        >
          <ActivityUserItem newValue={reviewer} />
        </ActivityItem.Section>
      ))}
    </>
  );
};

const hasReviewsChanged = (
  oldValue: ReviewsData | null,
  newValue: ReviewsData | null,
): boolean => {
  if (oldValue?.status !== newValue?.status) return true;
  return getChangedReviewers(oldValue, newValue).length > 0;
};

// Export for testing
export {
  collectResponses,
  formatReviewerLabel,
  getChangedReviewers,
  hasReviewsChanged,
};

export const REVIEWS_CHANGE_EXTENSION = createWorkflowUpdatesChangeExtension({
  id: 'reviews-change',
  predicate: ({ change }) => {
    if (change.fieldPath !== '/module_data/reviews') return false;
    return hasReviewsChanged(
      change.oldValue as ReviewsData | null,
      change.newValue as ReviewsData | null,
    );
  },
  title: () => 'Updated reviews',
  render: ReviewsChangeRender,
});
